import { FormEvent, useEffect, useState } from "react";
import { ArrowLeft, Eye, Save, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminStaticPage,
  AdminUser,
  getAdminStaticPage,
  getCurrentAdmin,
  logout,
  saveAdminStaticPage,
  uploadAdminStaticPageImage,
} from "./api";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import { AdminFormNotice } from "./components/AdminFormNotice";
import { AdminRichTextEditor } from "./components/AdminRichTextEditor";

type FormState = {
  Slug: string;
  Title: string;
  Content: string;
  IsPublished: boolean;
};

const emptyForm: FormState = { Slug: "", Title: "", Content: "", IsPublished: true };

function RequiredMark() {
  return <span className="ml-1 text-[#c3423f]" aria-hidden="true">*</span>;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function itemToForm(item: AdminStaticPage): FormState {
  return { Slug: item.Slug, Title: item.Title, Content: item.Content, IsPublished: item.IsPublished };
}

function StaticPagePreviewOverlay({ form, onClose }: { form: FormState; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] overflow-auto bg-white">
      <div className="sticky top-0 z-10 border-b border-[#dfe6e1] bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">自訂頁面預覽</div>
            <h2 className="text-xl font-bold text-[#1B3D2A]">{form.Title || "未命名頁面"}</h2>
          </div>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={onClose}>
            <X size={18} />
            關閉預覽
          </button>
        </div>
      </div>

      <main className="bg-white">
        <div className="mx-auto max-w-4xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          <h1 className="text-2xl font-bold leading-snug text-[#1B3D2A] md:text-3xl">{form.Title || "頁面標題"}</h1>
          <div className="my-6 h-px bg-[#E8E0D6]" />
          <div
            className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#1B3D2A] prose-a:text-[#1B3D2A] prose-img:rounded-xl prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-xl"
            dangerouslySetInnerHTML={{ __html: form.Content || "<p>頁面內容預覽</p>" }}
          />
        </div>
      </main>
    </div>
  );
}

export default function AdminStaticPageForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const staticPageId = id || null;
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(Boolean(staticPageId));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    async function init() {
      try {
        const me = await getCurrentAdmin();
        if (!alive) return;
        setCurrentUser(me.User);
        if (staticPageId) {
          const data = await getAdminStaticPage(staticPageId);
          if (alive) {
            setForm(itemToForm(data.Item));
            setSlugTouched(true);
          }
        }
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "頁面資料載入失敗。");
        navigate("/admin-login", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    }
    void init();
    return () => {
      alive = false;
    };
  }, [staticPageId, navigate]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleTitleChange(value: string) {
    setForm((current) => ({
      ...current,
      Title: value,
      Slug: slugTouched ? current.Slug : slugify(value),
    }));
  }

  function validate() {
    const errors: string[] = [];
    if (!form.Title.trim()) errors.push("請輸入頁面標題。");
    if (!form.Slug.trim()) errors.push("請輸入網址代稱。");
    if (!form.Content.replace(/<[^>]*>/g, "").trim()) errors.push("請輸入頁面內容。");
    return errors;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validate();
    if (errors.length > 0) {
      setMessage(errors[0]);
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const saved = await saveAdminStaticPage(staticPageId, {
        Slug: slugify(form.Slug),
        Title: form.Title.trim(),
        Content: form.Content,
        IsPublished: form.IsPublished,
      });
      navigate(`/admin/static-pages/${saved.Item.StaticPageId}/edit`, { replace: true });
      setMessage("頁面已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "頁面儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout sectionLabel="網站設定" title={staticPageId ? "編輯自訂頁面" : "新增自訂頁面"} user={currentUser} onLogout={handleLogout}>
      <form onSubmit={handleSubmit}>
        <div className="sticky top-[76px] z-10 mb-5 rounded-md border border-[#dfe6e1] bg-white/95 p-3 shadow-[0_10px_24px_rgba(32,61,50,.08)] backdrop-blur md:top-[110px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate("/admin/static-pages")} type="button">
              <ArrowLeft size={18} />
              回頁面列表
            </button>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setPreviewOpen((current) => !current)} type="button">
                <Eye size={18} />
                {previewOpen ? "關閉預覽" : "預覽"}
              </button>
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving || loading} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存頁面"}
              </button>
            </div>
          </div>
        </div>

        <AdminFormNotice message={message} />

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">載入中...</div>
        ) : (
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">基本資訊</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-1 md:col-span-2">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="page-title">頁面標題<RequiredMark /></label>
                    <input id="page-title" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Title} onChange={(event) => handleTitleChange(event.target.value)} />
                  </div>
                  <div className="grid gap-1 md:col-span-2">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="page-slug">網址代稱 Slug<RequiredMark /></label>
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap text-sm text-[#9a8a7a]">/page/</span>
                      <input
                        id="page-slug"
                        className="h-11 flex-1 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                        value={form.Slug}
                        onChange={(event) => {
                          setSlugTouched(true);
                          setField("Slug", slugify(event.target.value));
                        }}
                        placeholder="privacy-policy"
                      />
                    </div>
                    <p className="text-xs text-[#9a8a7a]">此欄位會決定前台網址，若此頁面已被其他地方連結（例如頁尾），修改後請記得同步更新連結。</p>
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">頁面內容<RequiredMark /></h2>
                <AdminRichTextEditor value={form.Content} onChange={(value) => setField("Content", value)} onUploadImage={uploadAdminStaticPageImage} imageAlt="頁面內容圖片" />
              </section>
            </div>

            <aside className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">發布設定</h2>
                <AdminSwitchCard checked={form.IsPublished} description="關閉後前台網址會顯示找不到頁面。" label="發布頁面" onChange={(checked) => setField("IsPublished", checked)} />
              </section>
            </aside>
          </div>
        )}
      </form>
      {previewOpen && <StaticPagePreviewOverlay form={form} onClose={() => setPreviewOpen(false)} />}
    </AdminLayout>
  );
}
